import { C } from "@/brand/tokens";
import { problemPatterns } from "@/brand/content";
import { ChamberChrome } from "@/components/common/ChamberChrome";

/**
 * ProblemRecognitionSection — Smoked Ink chamber (II. RECOGNITION).
 *
 * Authoring moves:
 *   - Chamber chrome rail at top
 *   - Vertical roman-numeral running head ("II / RECOGNITION") on the
 *     left viewport margin
 *   - Intro rail col 1-4
 *   - Staggered offset list col 6-12 — odd items align col 6-10,
 *     even items indent into col 8-12 (further into the right margin)
 *   - Each item's brass ordinal becomes a large display number set
 *     in the gutter, NOT inline with the text
 */
export function ProblemRecognitionSection() {
  return (
    <section
      id="who-we-help"
      data-testid="problem-recognition"
      style={{
        background: C.bgDark,
        color: C.textOnDark,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <ChamberChrome
        numeral="II."
        title="Recognition"
        category="Who we help"
        meta="Four patterns"
        tone="dark"
      />

      {/* Vertical roman-numeral running head — viewport left margin */}
      <span
        aria-hidden="true"
        className="hidden xl:flex absolute items-center"
        style={{
          left: 24,
          top: "8rem",
          bottom: "4rem",
          width: 24,
          writingMode: "vertical-rl",
          transform: "rotate(180deg)",
        }}
      >
        <span
          className="font-inter text-[10px] uppercase font-bold tabular-nums"
          style={{ color: C.brassLine, letterSpacing: "0.42em" }}
        >
          II &nbsp;·&nbsp; Recognition &nbsp;·&nbsp; Who We Help
        </span>
      </span>

      <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-14 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-y-16 lg:gap-x-10">
          {/* Intro rail */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-7">
              <span
                aria-hidden="true"
                className="inline-block h-px w-10"
                style={{ background: C.accentMetal }}
              />
              <p
                className="font-inter text-[11px] uppercase font-semibold tabular-nums"
                style={{ color: C.accentMetal, letterSpacing: "0.28em" }}
              >
                § 02
              </p>
            </div>
            <h2
              className="font-cormorant"
              style={{
                color: C.textOnDark,
                fontSize: "clamp(2rem, 3.6vw, 3rem)",
                fontWeight: 400,
                lineHeight: 1.04,
                letterSpacing: "-0.01em",
              }}
            >
              The patterns
              <br />
              capable leaders
              <br />
              recognize —
              <br />
              and quietly carry
              <br />
              alone.
            </h2>
            <p
              className="mt-10 font-inter leading-[1.65] max-w-[36ch]"
              style={{ color: C.whiteOnDark, fontSize: "1rem" }}
            >
              Most organizations don’t reach out because everything is easy.
              They reach out because a person, a team, or a decision is
              asking for more than the existing structure can hold.
            </p>
          </div>

          {/* Staggered list with brass numerals in the gutter */}
          <div
            className="lg:col-span-8 lg:pl-12 lg:border-l"
            style={{ borderColor: C.brassSoft }}
          >
            <ol className="space-y-0">
              {problemPatterns.map((p, i) => {
                const indented = i % 2 === 1; // 2nd & 4th push further right
                return (
                  <li
                    key={p.title}
                    data-testid={`problem-pattern-${i}`}
                    className="relative grid grid-cols-12 gap-x-6 py-14"
                    style={{
                      borderTop: i === 0 ? "none" : `1px solid ${C.mistOver}`,
                    }}
                  >
                    {/* Gutter number — col 1 */}
                    <div
                      className={`col-span-2 ${
                        indented ? "lg:col-start-3" : "lg:col-start-1"
                      } lg:col-span-2 flex items-start justify-end pt-1`}
                    >
                      <span
                        className="font-cormorant tabular-nums"
                        style={{
                          color: C.accentMetal,
                          fontSize: "clamp(2.6rem, 4vw, 3.6rem)",
                          fontWeight: 400,
                          lineHeight: 1,
                          letterSpacing: "-0.02em",
                        }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>

                    {/* Body — cols 5/3 - 12 / 10 (asymmetric) */}
                    <div
                      className={`col-span-10 ${
                        indented
                          ? "lg:col-start-6 lg:col-span-7"
                          : "lg:col-start-4 lg:col-span-7"
                      }`}
                    >
                      <p
                        className="font-inter text-[10.5px] uppercase font-semibold mb-5"
                        style={{
                          color: C.accentMetal,
                          letterSpacing: "0.26em",
                        }}
                      >
                        {p.tag}
                      </p>
                      <h3
                        className="font-cormorant"
                        style={{
                          color: C.textOnDark,
                          fontSize: "clamp(1.45rem, 2vw, 1.8rem)",
                          fontWeight: 400,
                          lineHeight: 1.16,
                          letterSpacing: "-0.005em",
                        }}
                      >
                        {p.title}
                      </h3>
                      <p
                        className="mt-5 font-inter leading-[1.7] max-w-[50ch]"
                        style={{
                          color: C.whiteOnDark,
                          fontSize: "0.98rem",
                        }}
                      >
                        {p.body}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}
